import React, { useState, useEffect } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { Users, Activity, AlertTriangle, CheckCircle, XCircle, TrendingUp } from "lucide-react";
import { AlertBanner } from "@/components/AlertBanner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminDashboard() {
  const { adminKey } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [webhookHealth, setWebhookHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [adminKey]);

  const fetchDashboardData = async () => {
    try {
      const headers = { "X-Admin-Key": adminKey };
      
      const [clientsRes, healthRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/clients?limit=10`, { headers }),
        fetch(`${API_URL}/api/admin/webhook-health`, { headers })
      ]);

      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setClients(data.clients || []);
      }

      if (healthRes.ok) {
        setWebhookHealth(await healthRes.json());
      }
    } catch (err) {
      console.error("Failed to fetch dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stats from clients
  const totalClients = clients.length;
  const liveClients = clients.filter(c => c.onboarding_status === "live").length;
  const blockedClients = clients.filter(c => !c.allowed).length;
  const suspendedClients = clients.filter(c => c.onboarding_status === "suspended").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4F80F5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Admin Overview</h1>

      {/* Alert Banner */}
      <AlertBanner adminKey={adminKey} />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard 
          title="Total Clients" 
          value={totalClients} 
          icon={Users}
          color="blue"
        />
        <StatCard 
          title="Live Clients" 
          value={liveClients} 
          icon={CheckCircle}
          color="green"
        />
        <StatCard 
          title="Calls Blocked" 
          value={blockedClients} 
          icon={XCircle}
          color="red"
        />
        <StatCard 
          title="Quarantined Events" 
          value={webhookHealth?.quarantined_total || 0} 
          icon={AlertTriangle}
          color="orange"
        />
      </div>

      {/* Webhook Health Banner */}
      <div className={`rounded-xl p-4 border shadow-sm ${
        webhookHealth?.status === "healthy" 
          ? "bg-green-50 border-green-200" 
          : "bg-red-50 border-red-200"
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className={`w-6 h-6 ${
              webhookHealth?.status === "healthy" ? "text-green-600" : "text-red-600"
            }`} />
            <div>
              <h3 className={`font-semibold ${webhookHealth?.status === "healthy" ? "text-green-800" : "text-red-800"}`}>
                Webhook Health: {webhookHealth?.status?.toUpperCase()}
              </h3>
              <p className="text-gray-600 text-sm">
                Last hour: {webhookHealth?.last_hour?.processed || 0} processed, {webhookHealth?.last_hour?.failed || 0} failed
              </p>
            </div>
          </div>
          <Link 
            to="/admin/webhook-health"
            className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm shadow-sm"
          >
            View Details
          </Link>
        </div>
      </div>

      {/* Recent Clients */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-gray-800 font-semibold">Recent Clients</h2>
          <Link to="/admin/clients" className="text-sm text-[#4F80F5] hover:text-[#3D6FE4]">
            View All →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Billing</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Calls Allowed</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {clients.slice(0, 5).map((client, idx) => (
                <tr key={client.id || idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-gray-800 font-medium">{client.name || "Unnamed"}</p>
                      <p className="text-gray-500 text-xs">{client.email || client.id}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={client.onboarding_status} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={client.billing_status} type="billing" />
                  </td>
                  <td className="px-4 py-3">
                    {client.allowed ? (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-4 h-4" /> Yes
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-600">
                        <XCircle className="w-4 h-4" /> No
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickActionCard 
          title="View Quarantine" 
          description="Review failed webhook events"
          to="/admin/quarantine"
          icon={AlertTriangle}
          color="orange"
        />
        <QuickActionCard 
          title="Manage Clients" 
          description="Activate, suspend, or grant grace"
          to="/admin/clients"
          icon={Users}
          color="blue"
        />
        <QuickActionCard 
          title="Audit Logs" 
          description="Review admin actions"
          to="/admin/audit-logs"
          icon={Activity}
          color="purple"
        />
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  const colors = {
    blue: "bg-[#4F80F5]",
    green: "bg-green-500",
    red: "bg-red-500",
    orange: "bg-orange-500",
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm">{title}</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status, type }) {
  const getStyle = () => {
    if (type === "billing") {
      switch (status) {
        case "active": return "bg-green-100 text-green-700";
        case "usage_exceeded": return "bg-red-100 text-red-700";
        case "suspended": return "bg-red-100 text-red-700";
        default: return "bg-gray-100 text-gray-600";
      }
    }
    switch (status) {
      case "live": return "bg-green-100 text-green-700";
      case "in_review": return "bg-blue-100 text-blue-700";
      case "pending_setup": return "bg-yellow-100 text-yellow-700";
      case "suspended": return "bg-red-100 text-red-700";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStyle()}`}>
      {status || "unknown"}
    </span>
  );
}

function QuickActionCard({ title, description, to, icon: Icon, color }) {
  const colors = {
    orange: "bg-orange-500",
    blue: "bg-[#4F80F5]",
    purple: "bg-purple-500",
  };

  return (
    <Link 
      to={to}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:border-[#4F80F5] hover:shadow-md transition-all group shadow-sm"
    >
      <div className={`w-10 h-10 rounded-lg ${colors[color]} flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <h3 className="text-gray-800 font-semibold group-hover:text-[#4F80F5] transition-colors">{title}</h3>
      <p className="text-gray-500 text-sm mt-1">{description}</p>
    </Link>
  );
}

export default AdminDashboard;
